import { SAVED_RENDER_HISTORY_LIMIT } from './renderHistoryDisplay';

function storageKey(roomId, roomImageId) {
  return `photorealistic:local-renders:${String(roomId)}:${String(roomImageId)}`;
}

function getRenderResultSortTime(entry) {
  const submittedAt = entry?.submittedAt ?? entry?.submitted_at;
  if (submittedAt != null) {
    const submitted = Number(submittedAt);
    if (Number.isFinite(submitted)) return submitted;
  }

  const value =
    entry?.completedAt ??
    entry?.completed_at ??
    entry?.startedAt ??
    entry?.started_at ??
    entry?.createdAt ??
    entry?.created_at ??
    null;
  if (!value) return 0;
  const time = new Date(value).getTime();
  return Number.isFinite(time) ? time : 0;
}

function normalizeEntry(entry) {
  if (!entry || typeof entry !== 'object') return null;
  const resultS3Key =
    entry.resultS3Key ?? entry.result_s3_key ?? entry.taskResult?.result_s3_key ?? '';
  const payload = entry.payload ?? null;
  const createdAt = entry.createdAt ?? entry.created_at ?? null;
  const startedAt = entry.startedAt ?? entry.started_at ?? null;
  const completedAt = entry.completedAt ?? entry.completed_at ?? null;
  const taskId = entry.taskId ?? entry.task_id ?? null;
  const inferenceId = entry.inferenceId ?? entry.inference_id ?? entry.id ?? null;
  const id =
    entry.id ??
    taskId ??
    (inferenceId != null ? String(inferenceId) : null) ??
    (completedAt ? `local-${completedAt}` : createdAt ? `local-${createdAt}` : null);
  if (!id || !resultS3Key || !payload) return null;

  return {
    id: String(id),
    taskId: taskId != null ? String(taskId) : null,
    inferenceId: inferenceId != null ? String(inferenceId) : null,
    resultS3Key: String(resultS3Key).trim(),
    payload,
    renderSettings: entry.renderSettings ?? null,
    submittedAt: entry.submittedAt ?? entry.submitted_at ?? null,
    createdAt,
    startedAt,
    completedAt,
  };
}

export function loadLocalRenderResults(roomId, roomImageId) {
  if (roomId == null || roomImageId == null) return [];
  try {
    const raw = localStorage.getItem(storageKey(roomId, roomImageId));
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return sortNewestFirst(parsed.map(normalizeEntry).filter(Boolean));
  } catch {
    return [];
  }
}

function sortNewestFirst(items) {
  return [...items].sort((a, b) => {
    const ta = getRenderResultSortTime(a);
    const tb = getRenderResultSortTime(b);
    if (tb !== ta) return tb - ta;
    return String(b?.id ?? '').localeCompare(String(a?.id ?? ''));
  });
}

export function saveLocalRenderResults(roomId, roomImageId, items) {
  if (roomId == null || roomImageId == null) return;
  try {
    const normalized = sortNewestFirst(items.map(normalizeEntry).filter(Boolean)).slice(
      0,
      SAVED_RENDER_HISTORY_LIMIT,
    );
    if (normalized.length === 0) {
      localStorage.removeItem(storageKey(roomId, roomImageId));
    } else {
      localStorage.setItem(storageKey(roomId, roomImageId), JSON.stringify(normalized));
    }
  } catch (err) {
    console.error('Failed to save local render results', err);
  }
}

export function appendLocalRenderResult(roomId, roomImageId, entry) {
  const normalized = normalizeEntry(entry);
  if (!normalized || roomId == null || roomImageId == null) return null;

  const existing = loadLocalRenderResults(roomId, roomImageId).filter(
    (item) => item.id !== normalized.id,
  );
  const next = sortNewestFirst([normalized, ...existing]).slice(0, SAVED_RENDER_HISTORY_LIMIT);
  saveLocalRenderResults(roomId, roomImageId, next);
  return normalized;
}

export function removeLocalRenderResult(roomId, roomImageId, entryId) {
  if (roomId == null || roomImageId == null || entryId == null) return;
  const next = loadLocalRenderResults(roomId, roomImageId).filter(
    (item) => String(item.id) !== String(entryId),
  );
  saveLocalRenderResults(roomId, roomImageId, next);
}

export function clearLocalRenderResults(roomId, roomImageId) {
  if (roomId == null || roomImageId == null) return;
  try {
    localStorage.removeItem(storageKey(roomId, roomImageId));
  } catch {
    /* ignore */
  }
}
